import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotionLogo, ArrowSquareOut } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable "Export to Notion" button.
 * - Probes /api/notion/settings on mount → enables/disables the button.
 * - If not configured: clicking redirects to Settings (#notion anchor).
 * - If configured: POSTs to /api/notion/export and toasts the resulting URL.
 */
export default function NotionExportButton({ shape_type, source_id, testId = 'btn-export-notion', variant = 'pill', className = '' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [configured, setConfigured] = useState(null); // null=loading, false, true
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/notion/settings`);
        if (alive) setConfigured(!!data?.configured);
      } catch {
        if (alive) setConfigured(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleClick = useCallback(async () => {
    if (!source_id) {
      toast.error(t('exp.nothing_to_export'));
      return;
    }
    if (configured === false) {
      toast.message(t('exp.configure_first', { service: 'Notion' }));
      navigate('/settings#notion');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/notion/export`, { shape_type, source_id });
      toast.success(t('exp.exported', { service: 'Notion' }), {
        description: data.title,
        action: data.notion_url ? {
          label: t('exp.open'),
          onClick: () => window.open(data.notion_url, '_blank', 'noopener,noreferrer'),
        } : undefined,
        duration: 8000,
      });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 412) {
        toast.message(t('exp.configure_first', { service: 'Notion' }));
        navigate('/settings#notion');
      } else {
        toast.error(detail || t('exp.export_fail', { service: 'Notion' }));
      }
    } finally {
      setBusy(false);
    }
  }, [configured, navigate, shape_type, source_id, t]);

  const isLoadingProbe = configured === null;
  const base = variant === 'ghost'
    ? 'inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition disabled:opacity-50'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs font-semibold transition disabled:opacity-50';

  return (
    <button
      type="button"
      data-testid={testId}
      data-configured={configured === true ? 'true' : 'false'}
      onClick={handleClick}
      disabled={busy || isLoadingProbe}
      title={configured === false ? t('exp.tooltip_configure', { service: 'Notion' }) : t('exp.tooltip_export', { service: 'Notion' })}
      className={`${base} ${className}`}
    >
      {busy ? (
        <>
          <ArrowSquareOut size={13} weight="bold" className="animate-pulse" /> {t('exp.exporting')}
        </>
      ) : (
        <>
          <NotionLogo size={13} weight="fill" /> Notion
        </>
      )}
    </button>
  );
}
